<template>
    <div class="yield-projection-chart">
        <div class="d-flex flex-wrap align-items-end gap-3 mb-3">
            <div>
                <label class="form-label small fw-bold mb-1">Stake Amount (SOL)</label>
                <input
                    type="number"
                    class="form-control form-control-sm font-mono-num"
                    v-model.number="stakeAmount"
                    min="1"
                    step="10"
                    style="max-width: 140px;"
                >
            </div>
            <div>
                <label class="form-label small fw-bold mb-1">Time Period</label>
                <select class="form-select form-select-sm" v-model="period" style="max-width: 130px;">
                    <option value="1">1 Year</option>
                    <option value="2">2 Years</option>
                    <option value="3">3 Years</option>
                    <option value="5">5 Years</option>
                </select>
            </div>
            <div class="flex-fill text-end">
                <div class="small text-muted">Projected Earnings</div>
                <div class="font-mono-num fw-bold pct-change pct-positive" style="font-size: 1.2rem;">
                    +{{ projectedEarnings.toFixed(2) }} SOL
                </div>
                <div class="small text-muted font-mono-num" v-if="solPrice">
                    ~${{ (projectedEarnings * solPrice).toLocaleString(undefined, { maximumFractionDigits: 0 }) }}
                </div>
            </div>
        </div>

        <canvas ref="chartCanvas" height="180"></canvas>

        <div class="d-flex justify-content-between small text-muted mt-2 px-1">
            <span>Compounds each epoch (~2.5 days)</span>
            <span>APY: <span class="font-mono-num fw-bold">{{ apy }}%</span></span>
        </div>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
    name: 'YieldProjectionChart',
    props: {
        apy: { type: Number, default: 7.2 },
        solPrice: { type: Number, default: null },
        initialAmount: { type: Number, default: 100 },
    },
    data() {
        return {
            chart: null,
            stakeAmount: this.initialAmount,
            period: 1,
        };
    },
    computed: {
        // ~146 epochs per year (365 / 2.5)
        epochsPerYear() { return 146; },
        totalEpochs() { return this.epochsPerYear * this.period; },
        epochRate() { return Math.pow(1 + this.apy / 100, 1 / this.epochsPerYear) - 1; },
        projectedEarnings() {
            const finalBalance = this.stakeAmount * Math.pow(1 + this.epochRate, this.totalEpochs);
            return finalBalance - this.stakeAmount;
        },
        projectionData() {
            const points = [];
            const monthsTotal = this.period * 12;
            const epochsPerMonth = this.epochsPerYear / 12;

            for (let m = 0; m <= monthsTotal; m++) {
                const epochs = m * epochsPerMonth;
                const balance = this.stakeAmount * Math.pow(1 + this.epochRate, epochs);
                points.push({
                    label: m === 0 ? 'Now' : m + 'mo',
                    balance: parseFloat(balance.toFixed(4)),
                    earnings: parseFloat((balance - this.stakeAmount).toFixed(4)),
                });
            }
            return points;
        },
    },
    watch: {
        stakeAmount() { this.renderChart(); },
        period() { this.renderChart(); },
    },
    mounted() {
        this.renderChart();
    },
    beforeUnmount() {
        if (this.chart) this.chart.destroy();
    },
    methods: {
        renderChart() {
            if (!this.$refs.chartCanvas) return;
            if (this.chart) this.chart.destroy();

            const ctx = this.$refs.chartCanvas.getContext('2d');
            const data = this.projectionData;

            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: data.map(d => d.label),
                    datasets: [
                        {
                            label: 'Total Balance (SOL)',
                            data: data.map(d => d.balance),
                            borderColor: 'rgb(16, 185, 129)',
                            backgroundColor: 'rgba(16, 185, 129, 0.08)',
                            fill: true,
                            tension: 0.3,
                            borderWidth: 2,
                            pointRadius: 0,
                            pointHoverRadius: 4,
                        },
                        {
                            label: 'Earnings (SOL)',
                            data: data.map(d => d.earnings),
                            borderColor: 'rgb(99, 102, 241)',
                            backgroundColor: 'rgba(99, 102, 241, 0.08)',
                            fill: true,
                            tension: 0.3,
                            borderWidth: 2,
                            borderDash: [4, 4],
                            pointRadius: 0,
                            pointHoverRadius: 4,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: {
                            display: true,
                            position: 'top',
                            labels: { boxWidth: 12, font: { size: 11 }, color: '#888' },
                        },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => {
                                    return `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SOL`;
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: {
                                maxTicksLimit: 12,
                                font: { size: 11 },
                                color: '#888',
                            },
                        },
                        y: {
                            grid: { color: 'rgba(255,255,255,0.05)' },
                            ticks: {
                                font: { size: 11 },
                                color: '#888',
                                callback: (val) => val.toLocaleString() + ' SOL',
                            },
                        },
                    },
                },
            });
        },
    },
};
</script>
